export enum SubscriptionPlanType {
  BASIC = 1,
  BEGINNER = 2,
  INTERMEDIATE = 3,
  PRO = 4,
  FREE = 5,
  STARTER = 6,
  ADVANCED = 7,
  PREMIUM = 8,
  PRO_PLUS_NEW = 9,
}

export const SUBSCRIPTION_PLAN_DISPLAYS = [
  "Pro",
  "Intermediate",
  "Beginner",
] as const;
export const SUBSCRIPTION_PLAN_TYPES = Object.values(
  SubscriptionPlanType
).filter((planType) => !isNaN(Number(planType))) as SubscriptionPlanType[];
export const PURCHASE_SUBSCRIPTION_TYPES = [
  ...SUBSCRIPTION_PLAN_TYPES,
  "Group",
] as const;

export type SubscriptionPlanDisplay =
  (typeof SUBSCRIPTION_PLAN_DISPLAYS)[number];
export type PurchaseSubscriptionType = SubscriptionPlanType | "Group";

export const EXTEND_SUBSCRIPTION_DURATIONS = [30, 360];
export const SUBSCRIPTION_DURATIONS = [30, 90, 180, 360];
export type SubscriptionDuration = (typeof SUBSCRIPTION_DURATIONS)[number];

export interface BasePlanDetails {
  discount?: number;
  price: number;
  subscription_days: SubscriptionDuration;
  subscription_plan_id: number;
}

export interface PlanDetails extends BasePlanDetails {
  subscription_features: string[] | null;
  subscription_type: SubscriptionPlanDisplay;
  subscription_plan_type: SubscriptionPlanType;
  upgrade_price?: number;
  days_left?: number;
  description?: string;
  image?: string;
}

export interface BillingAgreementDetails extends BasePlanDetails {
  payer_view_url?: string;
  payment_type: PaymentType;
  sub_group_image?: string;
  sub_group_name?: string;
  upgrade_price?: number;
  subscription_type?: SubscriptionPlanDisplay;
}

export enum PaymentType {
  EXTENSION = 1,
  UPGRADE = 2,
  LICENSES = 3,
  SLOTS = 4,
}

export type FormattedPlanDetails = Pick<
  PlanDetails,
  "subscription_features"
> & {
  [key in SubscriptionDuration]?: BasePlanDetails;
} & {
  upgradePlan?: PlanDetails;
  subscription_type?: SubscriptionPlanDisplay;
  subscription_plan_type?: SubscriptionPlanType;
};
export type FormattedPlanDetailsMapping = Record<
  PurchaseSubscriptionType,
  FormattedPlanDetails
>;

export enum SubscriptionPaymentMethodType {
  PAYPAL = 1,
  BTC = 2,
  LTC = 4,
  XRP = 5,
  USDT_TRC20 = 7,
  USDT_ERC20 = 8,
  USDC_ERC20 = 9,
  USDC_TRC20 = 10,
}

export interface PaymentMethod {
  id: number;
  payment_option: string;
  is_recurring: boolean;
  icon_url: string;
}

interface GetUpgradePaymentLinkRequest {
  payment_option: string;
  payment_type: PaymentType.UPGRADE;
  plan_type: PurchaseSubscriptionType;
  promo_code?: string;
}

export enum SubscriptionType {
  PLATFORM = 1,
  GROUP = 2,
}

interface BaseExtendPaymentLinkRequest {
  payment_option: string;
  payment_type: PaymentType.EXTENSION;
  promo_code?: string;
}

type GetExtendPaymentLinkRequest = BaseExtendPaymentLinkRequest &
  (
    | {
        subscription_plan_id: number;
      }
    | { group_subscription_plan_id: number; sub_group_id: number }
  );

type GetApiSlotsPaymentLinkRequest = {
  number_of_clients?: number;
  payment_type: PaymentType.SLOTS;
  payment_option: string;
};

export type GetPaymentLinkRequest = {
  force_invoice?: boolean;
  email: string;
} & (
  | GetUpgradePaymentLinkRequest
  | GetExtendPaymentLinkRequest
  | GetApiSlotsPaymentLinkRequest
);

export interface PaymentLinkData {
  payment_url: string;
  invoice_id: string;
  time_expires?: number;
}

export enum InvoiceStatus {
  PENDING = 1,
  INACTIVE = 2,
  EXPIRED = 3,
  PAID = 4,
  REVIEW = 5,
  DENIED = 6,
  REFUNDED = 7,
  REVERSED = 8,
  CANCELLED = 9,
}

export interface PaymentLinkStatus {
  payment_url: string;
  guest_payment_url?: string;
  amount?: string;
  payment_address?: string;
  received?: string;
  remaining?: string;
  time_expires?: number;
  qr_url?: string;
  status?: InvoiceStatus;
}

export interface PaymentDetails {
  cost?: number;
  duration: string;
  payer_view_url: string;
  payment_option: string;
  status: string;
  group_name?: string;
  subscription_type?: string;
  action: string;
  paid_datetime?: string;
  paid_timestamp?: number;
  creation_datetime?: string;
  creation_timestamp?: number;
  update_datetime?: string;
  update_timestamp?: number;
}

export type GetNewPaymentPriceResponse = {
  one_time_cost?: number;
  recurring_price?: number;
};

export type NewPaymentPriceResponse = GetNewPaymentPriceResponse & {
  message?: string;
  code?: number;
};

export type PromoCodePlansPricesType = {
  [key in SubscriptionDuration]: NewPaymentPriceResponse;
};

export enum UsageType {
  ACTIVE_TRADE = 1,
  CLIENT = 2,
  AUTO_TRADING_GROUP = 3,
  DCA_BOT = 4,
  GRID_BOT = 5,
  TV_BOT = 6,
  SIGNAL_BOT = 7,
  UI = 8,
  OPTIMIZED_CONFIGURATION = 9,
  AUTO_TRADING = 10,
  MANUAL_TRADE = 11,
  BACKTESTING = 12,
}

export type UsageLimitValue = number | boolean | "inf";

export type PlanUsageLimits = {
  [key in UsageType]?: UsageLimitValue;
};

export type PlansUsageLimits = {
  [key in SubscriptionPlanType]?: PlanUsageLimits;
};

export interface AdditionalUsageLimit {
  expiration_timestamp: number;
  quota: number;
  usage_type: UsageType;
}

export type AdditionalUsageLimits = AdditionalUsageLimit[];

export interface GetUsageLimitsResponse {
  additional_limits: AdditionalUsageLimits;
  limits: PlansUsageLimits;
}

export type ApprovePaymentRequest = BaseExtendPaymentLinkRequest & {
  transaction_id: string;
  user_uuid: string;
  product_id: string;
};
